/**
 * NeoCoreCobranzaCockpitPage — cockpit PREVENTIVO de cobranza para Nataly.
 *
 * Reorienta la cobranza de "perseguir mora" a "prevenir": muestra qué recordar
 * hoy (vence hoy / en 1-3 días), qué está vencido, y qué se pagó hoy. Cada fila
 * trae un botón WhatsApp con el mensaje precargado (wa.me) — un toque y listo.
 *
 * Fuente: RPC cobranza_cockpit() (todo en hora Guatemala). Solo admin.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowsClockwise, WhatsappLogo, ArrowRight } from '@phosphor-icons/react'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { formatQ } from '@/lib/format'

type Fila = {
  credito_id: string
  cuota_id: string
  asociado_id: string
  nombre: string | null
  no_asociado: string | null
  telefono: string | null
  monto: number
  vence: string
  dias_para: number
  dias_atraso: number | null
  mora: number | null
  tramo: string
  mensaje: string
}
type Cockpit = {
  generado_at: string
  hoy: string
  kpis: {
    cartera_activa: number; creditos_activos: number; al_dia_pct: number
    vencido_monto: number; vencido_n: number
    vencen_semana_monto: number; vencen_semana_n: number
    pagadas_hoy_n: number; pagadas_hoy_monto: number
  }
  worklist: Fila[]
}

function waLink(tel: string | null, msg: string): string | null {
  const num = (tel ?? '').replace(/\D/g, '')
  if (num.length < 8) return null
  return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`
}

export function NeoCoreCobranzaCockpitPage() {
  const [data, setData] = useState<Cockpit | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    setLoading(true); setError(null)
    supabase.rpc('cobranza_cockpit').then(({ data, error }) => {
      if (error) setError(error.message)
      else setData(data as Cockpit)
      setLoading(false)
    })
  }, [])
  useEffect(() => { load() }, [load])

  const grupos = useMemo(() => {
    const w = data?.worklist ?? []
    return {
      vencidas: w.filter((f) => f.dias_para < 0),
      hoy: w.filter((f) => f.dias_para === 0),
      proximas: w.filter((f) => f.dias_para > 0),
    }
  }, [data])

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--n-500)' }}>Cargando…</div>
  if (error) return <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}><div className="error-banner" style={{ margin: 0 }}>{error}</div><button className="btn" onClick={load}>Reintentar</button></div>
  if (!data) return null
  const k = data.kpis

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <p style={{ margin: 0, color: 'var(--n-500)', fontSize: 13 }}>Cobranza del día en hora Guatemala. Recordá antes de que venza; un toque abre WhatsApp con el mensaje listo.</p>
        <button className="btn btn-ghost" onClick={load}><ArrowsClockwise size={16} /> Actualizar</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12 }}>
        <Kpi label="Al día" value={`${k.al_dia_pct}%`} sub={`${k.creditos_activos} créditos activos`} tone="ok" />
        <Kpi label="Vencido" value={formatQ(k.vencido_monto)} sub={`${k.vencido_n} cuotas`} tone={k.vencido_n > 0 ? 'warn' : undefined} />
        <Kpi label="Vence esta semana" value={formatQ(k.vencen_semana_monto)} sub={`${k.vencen_semana_n} cuotas`} />
        <Kpi label="Pagado hoy" value={formatQ(k.pagadas_hoy_monto)} sub={`${k.pagadas_hoy_n} cuotas`} tone="ok" />
      </div>

      <Seccion titulo={`Vencidas · ${grupos.vencidas.length}`} filas={grupos.vencidas} vacio="Nada vencido. 🎉" />
      <Seccion titulo={`Vencen hoy · ${grupos.hoy.length}`} filas={grupos.hoy} vacio="Nada vence hoy." />
      <Seccion titulo={`Próximas (1-3 días) · ${grupos.proximas.length}`} filas={grupos.proximas} vacio="Nada en los próximos 3 días." />

      <p style={{ color: 'var(--n-400)', fontSize: 11, margin: 0 }}>
        Actualizado: {new Date(data.generado_at).toLocaleString('es-GT')} · hoy (GT): {data.hoy}
      </p>
    </div>
  )
}

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'ok' | 'warn' }) {
  const color = tone === 'ok' ? 'var(--ok, #1E9E63)' : tone === 'warn' ? 'var(--brand-700, #C2410C)' : 'var(--n-800)'
  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ fontSize: 12, color: 'var(--n-500)' }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, marginTop: 4, color }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--n-400)', marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

function Seccion({ titulo, filas, vacio }: { titulo: string; filas: Fila[]; vacio: string }) {
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--n-100)', fontWeight: 700, fontSize: 14 }}>{titulo}</div>
      {filas.length === 0 ? (
        <div style={{ padding: 20, textAlign: 'center', color: 'var(--n-400)', fontSize: 13 }}>{vacio}</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <tbody>
              {filas.map((f) => {
                const link = waLink(f.telefono, f.mensaje)
                const cuando = f.dias_para < 0 ? `venció hace ${Math.abs(f.dias_para)}d` : f.dias_para === 0 ? 'vence hoy' : `en ${f.dias_para}d`
                return (
                  <tr key={f.cuota_id}>
                    <td style={{ padding: '10px 16px', borderBottom: '1px solid var(--n-50)' }}>
                      <div style={{ fontWeight: 600 }}>{f.nombre ?? '—'}</div>
                      <div style={{ fontSize: 11, color: 'var(--n-400)' }}>Neo.id {f.no_asociado ?? '—'} · vence {new Date(f.vence + 'T00:00:00').toLocaleDateString('es-GT', { day: '2-digit', month: '2-digit' })}</div>
                    </td>
                    <td style={{ padding: '10px 16px', borderBottom: '1px solid var(--n-50)', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 700 }}>{formatQ(f.monto)}</div>
                      <div style={{ fontSize: 11, color: f.dias_para < 0 ? 'var(--brand-700, #C2410C)' : 'var(--n-400)' }}>
                        {cuando}{f.mora && f.mora > 0 ? ` · mora ${formatQ(f.mora)}` : ''}
                      </div>
                    </td>
                    <td style={{ padding: '10px 16px', borderBottom: '1px solid var(--n-50)', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {link ? (
                        <a className="btn" href={link} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px' }}>
                          <WhatsappLogo size={16} /> Recordar
                        </a>
                      ) : (
                        <span style={{ fontSize: 11, color: 'var(--n-400)' }}>sin teléfono</span>
                      )}
                      <Link to={`/neocore/creditos/${f.credito_id}`} style={{ marginLeft: 8, color: 'var(--n-500)', verticalAlign: 'middle' }} title="Ver crédito"><ArrowRight size={16} /></Link>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default NeoCoreCobranzaCockpitPage
